import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  getTestimonials,
  createTestimonial,
  updateTestimonial,
  toggleTestimonial,
  deleteTestimonial,
} from '../../services/testimonialApi';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  RefreshCw,
  Search,
  Sparkles,
  CheckCircle2,
  XCircle,
  Star,
  Globe,
  Upload,
  Image as ImageIcon,
  Loader2,
  Info,
  ShieldAlert,
  ArrowUpDown,
  SlidersHorizontal,
} from 'lucide-react';
import { getAvatarUrl } from '../../utils/avatar';

const emptyForm = {
  name: '',
  role: '',
  department: '',
  message: '',
  avatar: '',
  rating: 5,
  displayOrder: 0,
  isActive: true,
};

export default function TestimonialList() {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const canManage = ['admin', 'customer_care', 'cc_head'].includes(user?.role);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const res = await getTestimonials({
        isActive: statusFilter === 'all' ? undefined : statusFilter,
        search: searchTerm.trim() || undefined,
      });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setTestimonials(list);
    } catch (error) {
      console.error('Failed to load testimonials:', error);
      toast.error('Failed to load testimonials');
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, [statusFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTestimonials();
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({
      ...emptyForm,
      displayOrder: testimonials.length + 1,
    });
    setShowModal(true);
  };

  const openEditModal = (t) => {
    setEditingId(t._id);
    setFormData({
      name: t.name || '',
      role: t.role || '',
      department: t.department || '',
      message: t.message || '',
      avatar: t.avatar || '',
      rating: t.rating ?? 5,
      displayOrder: t.displayOrder ?? 0,
      isActive: t.isActive ?? true,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be under 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadData = new FormData();
      uploadData.append('documents', file);

      const res = await api.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const uploadedUrl = res.data?.data?.files?.[0]?.url;
      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, avatar: uploadedUrl }));
        toast.success('Photo uploaded successfully');
      } else {
        throw new Error('No URL returned');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleTestimonial(id);
      setTestimonials((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
      toast.success(
        !currentStatus ? 'Testimonial is now live on the website!' : 'Testimonial hidden from website'
      );
    } catch (err) {
      console.error('Failed to toggle status:', err);
      toast.error('Failed to update status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.role.trim() || !formData.message.trim()) {
      toast.error('Please fill in Doctor Name, Role, and Testimonial Quote');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        role: formData.role.trim(),
        department: formData.department.trim(),
        message: formData.message.trim(),
        avatar: formData.avatar.trim(),
        rating: Number(formData.rating) || 5,
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: Boolean(formData.isActive),
      };

      if (editingId) {
        await updateTestimonial(editingId, payload);
        toast.success('Testimonial updated successfully');
      } else {
        await createTestimonial(payload);
        toast.success('Testimonial created and published');
      }

      closeModal();
      fetchTestimonials();
    } catch (err) {
      console.error('Failed to save testimonial:', err);
      toast.error(err.response?.data?.message || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this testimonial?')) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteTestimonial(id);
      toast.success('Testimonial deleted');
      setTestimonials((prev) => prev.filter((t) => t._id !== id));
    } catch (err) {
      console.error('Failed to delete testimonial:', err);
      toast.error('Failed to delete testimonial');
    } finally {
      setDeletingId(null);
    }
  };

  const totalCount = testimonials.length;
  const liveCount = testimonials.filter((t) => t.isActive).length;
  const hiddenCount = testimonials.filter((t) => !t.isActive).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* ── HEADER & ACTIONS ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <Globe size={22} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Website Testimonials
                </h1>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Website CMS
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Manage doctor quotes displayed in the "What Doctors Say" section of the public website.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchTestimonials}
            disabled={loading}
            className="p-2.5 text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>

          {canManage && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Plus size={18} />
              <span>Add Testimonial</span>
            </button>
          )}
        </div>
      </div>

      {/* ── IMPORTANT NOTICE BANNER ── */}
      <div className="bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50 border border-blue-200/80 rounded-2xl p-4 sm:p-5 flex items-start gap-3.5 shadow-sm">
        <div className="p-2 bg-blue-600 text-white rounded-xl flex-shrink-0 mt-0.5">
          <Info size={18} />
        </div>
        <div className="text-xs sm:text-sm text-slate-700 leading-relaxed">
          <p className="font-bold text-slate-900">Dedicated Website Presentation Content</p>
          <p className="text-slate-600 mt-0.5">
            These testimonials are used exclusively for public marketing display on the DOXEZ website.
            Adding, editing, or deleting testimonials here <strong>will not create, alter, or link to CRM onboarding doctor accounts</strong> or clinical credentials.
          </p>
        </div>
      </div>

      {/* ── KPI STATS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Testimonials</p>
            <p className="text-2xl font-black text-slate-900 mt-1">{totalCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <Sparkles size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Live On Website</p>
            <p className="text-2xl font-black text-slate-900 mt-1">{liveCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
            <CheckCircle2 size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Hidden / Inactive</p>
            <p className="text-2xl font-black text-slate-900 mt-1">{hiddenCount}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center font-bold">
            <XCircle size={22} />
          </div>
        </div>
      </div>

      {/* ── SEARCH & FILTER BAR ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-80">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by doctor name or role..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
          />
        </form>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600 w-full sm:w-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${
                statusFilter === 'all'
                  ? 'bg-white text-blue-600 shadow-sm font-bold'
                  : 'hover:text-slate-900'
              }`}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setStatusFilter('true')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${
                statusFilter === 'true'
                  ? 'bg-white text-emerald-600 shadow-sm font-bold'
                  : 'hover:text-slate-900'
              }`}
            >
              Live ({liveCount})
            </button>
            <button
              onClick={() => setStatusFilter('false')}
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-lg transition-all ${
                statusFilter === 'false'
                  ? 'bg-white text-slate-700 shadow-sm font-bold'
                  : 'hover:text-slate-900'
              }`}
            >
              Hidden ({hiddenCount})
            </button>
          </div>
        </div>
      </div>

      {/* ── TESTIMONIALS LIST / GRID ── */}
      {loading ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center justify-center space-y-3">
          <Loader2 size={32} className="animate-spin text-blue-600" />
          <p className="text-sm text-slate-500 font-medium">Loading testimonials...</p>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-slate-100 shadow-sm text-center">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe size={28} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">No Testimonials Found</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mt-1 mb-6">
            {searchTerm
              ? 'No testimonials matched your search query. Try clearing the search or adding a new testimonial.'
              : 'Add your first doctor testimonial to feature it on the website.'}
          </p>
          {canManage && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/20 transition-all"
            >
              <Plus size={18} />
              <span>Add New Testimonial</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t) => {
            const avatarSrc = t.avatar
              ? t.avatar.startsWith('http') || t.avatar.startsWith('/')
                ? t.avatar
                : getAvatarUrl(t.avatar)
              : null;

            return (
              <div
                key={t._id}
                className={`bg-white rounded-2xl border p-5 shadow-sm transition-all duration-200 flex flex-col justify-between hover:shadow-md ${
                  t.isActive ? 'border-slate-200/80' : 'border-slate-200 bg-slate-50/50 opacity-80'
                }`}
              >
                <div>
                  {/* Top Bar: Order & Live Badge */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="text-[11px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md flex items-center gap-1">
                      <ArrowUpDown size={12} />
                      Order: {t.displayOrder ?? 0}
                    </span>

                    <button
                      onClick={() => handleToggleStatus(t._id, t.isActive)}
                      className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 transition-all cursor-pointer ${
                        t.isActive
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                      title="Click to toggle website visibility"
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          t.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
                        }`}
                      />
                      {t.isActive ? 'Live on Website' : 'Hidden'}
                    </button>
                  </div>

                  {/* Doctor Info */}
                  <div className="flex items-center gap-3 mb-3">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover border border-slate-200 shadow-sm flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            'https://ui-avatars.com/api/?name=' +
                            encodeURIComponent(t.name) +
                            '&background=0284c7&color=fff';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center flex-shrink-0 shadow-sm">
                        {t.name
                          .split(' ')
                          .map((n) => n[0])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join('')}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                        {t.name}
                      </h3>
                      <p className="text-xs text-blue-600 font-semibold truncate">{t.role}</p>
                      {t.department && (
                        <p className="text-[11px] text-slate-500 truncate">{t.department}</p>
                      )}
                    </div>
                  </div>

                  {/* Star Rating */}
                  <div className="flex items-center gap-1 mb-2.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={
                          i < (t.rating || 5)
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-slate-200 fill-slate-200'
                        }
                      />
                    ))}
                    <span className="text-xs font-bold text-slate-600 ml-1">
                      {t.rating || 5}.0
                    </span>
                  </div>

                  {/* Quote Description */}
                  <p className="text-xs sm:text-sm text-slate-600 line-clamp-4 italic bg-slate-50 p-3 rounded-xl border border-slate-100 mb-4">
                    "{t.message}"
                  </p>
                </div>

                {/* Bottom Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-slate-400 text-[11px]">
                    Added {new Date(t.createdAt).toLocaleDateString()}
                  </span>

                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(t)}
                        className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Edit Testimonial"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(t._id)}
                        disabled={deletingId === t._id}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete Testimonial"
                      >
                        {deletingId === t._id ? (
                          <Loader2 size={16} className="animate-spin text-red-600" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── CREATE / EDIT MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-xl w-full shadow-2xl border border-slate-100 overflow-hidden my-8 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <span className="p-2 bg-blue-600 text-white rounded-xl">
                  {editingId ? <Edit2 size={18} /> : <Plus size={18} />}
                </span>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {editingId ? 'Edit Website Testimonial' : 'Add Website Testimonial'}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Publishes doctor review card onto the public website slider.
                  </p>
                </div>
              </div>

              <button
                onClick={closeModal}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3 text-xs text-blue-800 flex items-start gap-2">
                <Info size={16} className="flex-shrink-0 text-blue-600 mt-0.5" />
                <span>
                  <strong>Note:</strong> This entry is solely for website presentation and does not create or link to onboarding doctor accounts.
                </span>
              </div>

              {/* Doctor Name & Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Doctor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Dr. Samuel Jacob"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Role / Designation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g. Senior Anesthesiologist"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-medium"
                  />
                </div>
              </div>

              {/* Department & Display Order */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Department / Subtitle <span className="text-slate-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    placeholder="e.g. Department of Pediatrics"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Display Order <span className="text-slate-400 font-normal">(Lower shows first)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                    placeholder="1"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-medium"
                  />
                </div>
              </div>

              {/* Star Rating */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Rating (1 - 5 Stars)
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="p-1 text-slate-300 hover:text-amber-400 transition-colors"
                    >
                      <Star
                        size={22}
                        className={
                          star <= formData.rating
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-slate-200 fill-slate-200'
                        }
                      />
                    </button>
                  ))}
                  <span className="text-xs font-bold text-slate-600 ml-2">
                    {formData.rating} / 5 Stars
                  </span>
                </div>
              </div>

              {/* Profile Photo Upload / URL */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Doctor Profile Photo <span className="text-slate-400 font-normal">(File upload or Image URL)</span>
                </label>

                <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                  {/* Photo Preview */}
                  <div className="w-14 h-14 rounded-full bg-slate-200 border-2 border-white shadow flex items-center justify-center overflow-hidden flex-shrink-0">
                    {formData.avatar ? (
                      <img
                        src={
                          formData.avatar.startsWith('http') || formData.avatar.startsWith('/')
                            ? formData.avatar
                            : getAvatarUrl(formData.avatar)
                        }
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            'https://ui-avatars.com/api/?name=' +
                            encodeURIComponent(formData.name || 'Doctor') +
                            '&background=0284c7&color=fff';
                        }}
                      />
                    ) : (
                      <ImageIcon size={22} className="text-slate-400" />
                    )}
                  </div>

                  <div className="flex-1 w-full space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm transition-all"
                      >
                        {uploadingImage ? (
                          <Loader2 size={14} className="animate-spin text-blue-600" />
                        ) : (
                          <Upload size={14} />
                        )}
                        <span>{uploadingImage ? 'Uploading...' : 'Upload Image File'}</span>
                      </button>

                      {formData.avatar && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, avatar: '' })}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Remove Photo
                        </button>
                      )}
                    </div>

                    <input
                      type="text"
                      value={formData.avatar}
                      onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                      placeholder="Or paste an image URL (e.g. /assets/Arjun.jpg or https://...)"
                      className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Testimonial Quote / Description */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Testimonial Quote / Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Enter the doctor's feedback, experience, or quote..."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none leading-relaxed font-normal"
                />
              </div>

              {/* Live on Website Toggle */}
              <div className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                <div>
                  <p className="text-xs font-bold text-slate-800">Publish to Website</p>
                  <p className="text-[11px] text-slate-500">
                    Immediately show this testimonial in the website slider
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>

              {/* Form Buttons */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  <span>{editingId ? 'Update Testimonial' : 'Publish Testimonial'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
